import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useNavigate } from "react-router-dom";
import {
  createConsumerList,
  exportBillCycleList,
  getBillCycleConsumerDashboard,
  getBillCycleDetails,
  getBillCycleDetailsForEdit,
  getBillCycleList,
  getBillSummary,
  getCategoryConfigMap,
  getConsumerList,
  getCycleWiseRun,
  getMonthlyStats,
  getPremiseList,
  getSubCategoryConfigMap,
  getTerritoryConfigMap,
  submitBillCycle,
  updateBillCycle,
} from "./api";
import { useState } from "react";
import { downloadCSV } from "@shared/utils/utils";
import { toast, useToast } from "@shared/ui/use-toast";

// export const useBillCycleConsumerDashboard = (params: Record<string, any>) => {
//   return useSmartQuery(
//     [QueryKeyFactory.module.bx.billCycle.billCycleDashboard(), params],
//     () => getBillCycleConsumerDashboard(params)
//   );
// };

export const useBillCycleConsumerDashboard = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => getBillCycleConsumerDashboard(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Consumer Dashboard",
          description: "Consumer Data fetched successfully.",
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

// Hook for Bill Cycle List with pagination
export const useBillCycleList = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleList(), queryParams],
    () => getBillCycleList(queryParams)
  );
};

export const useBillCycleExport = (queryParams: Record<string, any> = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.exportList(), queryParams],
    async () => {
      setIsExporting(true);
      try {
        const blob = await exportBillCycleList(queryParams);
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export bill cycles: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

export const useBillCycleDetails = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDetails(), billCycleId],
    () => getBillCycleDetails(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useBillCycleDetailsForEdit = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDetailsEdit(), billCycleId],
    () => getBillCycleDetailsForEdit(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useCycleWiseRun = (
  billCycleId: string | number,
  page: number,
  search: string
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.cycleWiseRunList(),
      billCycleId,
      page,
      search,
    ],
    () => getCycleWiseRun(billCycleId, page, search)
  );
};

export const useMonthlyStats = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.monthlyStats(), billCycleId],
    () => getMonthlyStats(billCycleId)
  );
};

export const useCategoryConfigMap = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.category(), getRemoteUtilityId()],
    () => getCategoryConfigMap(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useSubCategoryConfigMap = (codes: string[]) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.bulkBillGeneration.subCategoryList(), codes],
    () => getSubCategoryConfigMap(codes),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

// export const useConsumerList = (
//   queryParams: Record<string, any>,
//   billCycleId?: string | number,
//   isEnabled?: boolean
// ) => {
//   return useSmartQuery(
//     [
//       QueryKeyFactory.module.bx.billCycle.consumerList(),
//       queryParams,
//       billCycleId,
//     ],
//     () => getConsumerList(queryParams, billCycleId),
//     {
//       enabled: isEnabled,
//     }
//   );
// };

export const useConsumerList = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => createConsumerList(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Consumer List",
          description: "Consumer List fetched successfully.",
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};


export const useExportConsumerList = (
  queryParams: Record<string, any>,
  billCycleId?: string | number,
  isEnabled?: boolean
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.consumerList(),
      queryParams,
      billCycleId,
    ],
    () => getConsumerList(queryParams, billCycleId),
    {
      staleTime: 0,
      enabled: isEnabled,
    }
  );
};
export const useBillSummary = () => {
  return useSmartQuery([QueryKeyFactory.module.bx.billCycle.summary], () =>
    getBillSummary()
  );
};

export const useSubmitBillCycle = () => {
  const navigate = useNavigate();
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => submitBillCycle(payload),
    {
      onSuccess: () => {
        navigate("/bx/bill-setup/billcycle");
        const key = QueryKeyFactory.module.bx.billCycle.billCycleList();

        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.refetchQueries({
          queryKey: key,
        });
      },
      onError: (error: Error) => {
        // toast.error(error.message);
      },
    }
  );
};

export const useAreaList = () => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.areaList(), getRemoteUtilityId()],
    () => getTerritoryConfigMap(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useSubAreaList = (areaList: string[]) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.subAreaList(), areaList],
    () => getTerritoryConfigMap("sub_area", areaList)
  );
};

export const usePremiseList = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.premiseList(), queryParams],
    () => getPremiseList(queryParams)
  );
};

export const useUpdateBillCycle = (billCycleId: string) => {
  const navigate = useNavigate();
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => updateBillCycle(payload, billCycleId),
    {
      onSuccess: () => {
        navigate("/bx/bill-setup/billcycle");
        const key = QueryKeyFactory.module.bx.billCycle.billCycleList();

        queryClient.invalidateQueries({
          queryKey: key,
          exact: false,
        });

        queryClient.refetchQueries({
          queryKey: key,
          exact: false,
        });
      },
      onError: (error: Error) => {
        // toast.error(error.message);
      },
    }
  );
};
